/**
 * Surface content — 2.5 Library · the requirements library.
 *
 * ZERO literals in the component (build brief §0a): every figure, name, route
 * and sentence this surface renders is declared here or read from
 * `@/demo/data`.
 *
 * The library's own claim is ANONYMITY. A requirement enters it stripped of
 * the client it came from; what travels is the pattern and the effort. So the
 * rows below carry NO client name and no project id — "solved by" cites the
 * template or the module that answers the pattern, never the engagement that
 * paid for it. That is not a data gap: it is the product's promise, and the
 * privacy panel says so in the surface.
 *
 * The group-level standard/library/new split is read from `VAN_DIJK.fitGap` —
 * the deck states one split, and the exemplar project happens to carry the
 * same three percentages, so both are sourced from the one declared fact
 * rather than re-typed here.
 */

import { PORTFOLIO, VAN_DIJK, thinSpace } from '@/demo/data'

const FIT_GAP = VAN_DIJK.fitGap

/* ── Routes — every affordance lands on a real surface ──────────────────── */

export const HREFS = {
  templates: '/library/templates',
  retailRollout: '/library/templates/retail-rollout',
  projects: '/projects',
  clients: '/clients',
} as const

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const LIBRARY_PAGE = {
  moduleLabel: 'PX · Library',
  breadcrumb: 'Library › Requirements',
  contextPill: 'ANONYMISED',
  title: 'Requirements library',
  standfirst:
    'Every requirement the group has ever captured, stripped of the client it came from, searchable as a ' +
    'pattern. When a consultant scopes a new client, PX shows how the same requirement was solved before and ' +
    'what it cost.',
  claim: 'It turns the group’s history into pricing power.',
} as const

/* ── How a pattern was solved ───────────────────────────────────────────── */

/**
 * `Template v4` is a rollout template PX proposes rather than builds;
 * `Module available` is already-built code in the library; `New` was a genuine
 * new build the first time it was asked for.
 */
export type SolvedBy = 'Template v4' | 'Module available' | 'New'

export const SOLVED_BY: Record<SolvedBy, SolvedBy> = {
  'Template v4': 'Template v4',
  'Module available': 'Module available',
  New: 'New',
} as const

/* ── Records ────────────────────────────────────────────────────────────── */

export type OdooVersion = 19 | 18 | 17

export interface LibraryRecord {
  id: string
  /** The anonymised requirement pattern — no client, no project, no place. */
  pattern: string
  /** One line of what the pattern actually asks for, still anonymised. */
  detail: string
  vertical: string
  odooVersion: OdooVersion
  solvedBy: SolvedBy
  /** The template or module that answers it — never the client that paid. */
  solution: string
  effortH: number
  /** Times the answer has been reused across the group since. */
  reuses: number
  /** Extra search terms a consultant would actually type. */
  keywords: readonly string[]
}

/**
 * Eight exemplar patterns. Four of them are the themes the Van Dijk register
 * carries (pricing hierarchy, overlapping promotions, loyalty import,
 * store-group lookup) — cross-referencing between the two surfaces is the
 * point of the library, so the same questions have to be findable here. The
 * rest come from other verticals and other offices.
 */
export const RECORDS: readonly LibraryRecord[] = [
  {
    id: 'rp-101',
    pattern: 'Store-level pricing hierarchy',
    detail: 'A group price list each store may override, with a deterministic fallback chain up the hierarchy.',
    vertical: 'Retail',
    odooVersion: 19,
    solvedBy: 'Template v4',
    solution: 'Retail rollout template v4 · pricing hierarchy',
    effortH: 24,
    reuses: 7,
    keywords: ['price list', 'override', 'fallback', 'pricelist'],
  },
  {
    id: 'rp-102',
    pattern: 'Overlapping promotions',
    detail: 'Two campaigns land on the same product; precedence and stacking resolve the same way every time.',
    vertical: 'Retail',
    odooVersion: 19,
    solvedBy: 'Template v4',
    solution: 'Retail rollout template v4 · promotion precedence',
    effortH: 32,
    reuses: 5,
    keywords: ['campaign', 'discount', 'stacking', 'precedence'],
  },
  {
    id: 'rp-103',
    pattern: 'Loyalty balance import from a legacy card scheme',
    detail: 'Historic point balances carried over, dormant cards included, with a reconciliation report.',
    vertical: 'Retail',
    odooVersion: 18,
    solvedBy: 'Module available',
    solution: 'px_loyalty_import',
    effortH: 18,
    reuses: 4,
    keywords: ['points', 'card', 'migration', 'dormant', 'reconciliation'],
  },
  {
    id: 'rp-104',
    pattern: 'Store-group lookup for reporting roll-up',
    detail: 'Store → region → banner, so one sales report answers at any level without a rebuild.',
    vertical: 'Retail',
    odooVersion: 19,
    solvedBy: 'Module available',
    solution: 'px_store_group',
    effortH: 12,
    reuses: 6,
    keywords: ['region', 'banner', 'hierarchy', 'reporting', 'roll-up'],
  },
  {
    id: 'rp-201',
    pattern: 'Factur-X e-invoicing for the French mandate',
    detail: 'PDF/A-3 invoices with embedded structured XML, validated before send and archived as issued.',
    vertical: 'Manufacturing',
    odooVersion: 19,
    solvedBy: 'Module available',
    solution: 'px_facturx_fr',
    effortH: 40,
    reuses: 9,
    keywords: ['france', 'fr', 'einvoicing', 'compliance', 'pdf', 'xml', 'chorus'],
  },
  {
    id: 'rp-202',
    pattern: 'MRP routing with a subcontracted operation step',
    detail: 'One operation leaves the shop floor mid-route and comes back costed, without splitting the order.',
    vertical: 'Manufacturing',
    odooVersion: 19,
    solvedBy: 'New',
    solution: 'New build — candidate for the library',
    effortH: 56,
    reuses: 0,
    keywords: ['routing', 'work order', 'subcontracting', 'costing', 'production'],
  },
  {
    id: 'rp-301',
    pattern: 'Patient intake consent capture and retention',
    detail: 'Consent recorded at intake with its purpose and expiry, and enforced on every later use of the record.',
    vertical: 'Healthcare',
    odooVersion: 18,
    solvedBy: 'New',
    solution: 'New build — candidate for the library',
    effortH: 44,
    reuses: 0,
    keywords: ['belgium', 'be', 'gdpr', 'privacy', 'intake', 'retention'],
  },
  {
    id: 'rp-401',
    pattern: 'Delivery slot booking against carrier capacity',
    detail: 'Slots reserved against a carrier’s daily capacity, released automatically when an order slips.',
    vertical: 'Logistics',
    odooVersion: 19,
    solvedBy: 'Module available',
    solution: 'px_slot_booking',
    effortH: 28,
    reuses: 3,
    keywords: ['carrier', 'capacity', 'scheduling', 'dock', 'transport'],
  },
] as const

/* ── Where a "solved by" pill leads ─────────────────────────────────────── */

const RETAIL = 'Retail'

/**
 * The pill is the way into the answer: a retail template opens the rollout
 * template itself, a library module opens the template & module library. A
 * new build has nothing to open yet — and never links to the client it was
 * built for, because the library does not hold that.
 */
export const solutionHref = (record: LibraryRecord): string | null => {
  if (record.solvedBy === SOLVED_BY['Template v4']) {
    return record.vertical === RETAIL ? HREFS.retailRollout : HREFS.templates
  }
  if (record.solvedBy === SOLVED_BY['Module available']) return HREFS.templates
  return null
}

/* ── Filters (all derived from the records — never typed twice) ─────────── */

export const ALL = 'all'

export interface Chip {
  key: string
  label: string
  count: number
}

const uniqueInOrder = <T,>(values: readonly T[]): readonly T[] => Array.from(new Set(values))

const VERTICALS = uniqueInOrder(RECORDS.map((r) => r.vertical))
const VERSIONS = uniqueInOrder(RECORDS.map((r) => r.odooVersion)).slice().sort((a, b) => b - a)

export const VERTICAL_CHIPS: readonly Chip[] = [
  { key: ALL, label: 'All verticals', count: RECORDS.length },
  ...VERTICALS.map((v) => ({
    key: v,
    label: v,
    count: RECORDS.filter((r) => r.vertical === v).length,
  })),
]

export const VERSION_CHIPS: readonly Chip[] = [
  { key: ALL, label: 'All versions', count: RECORDS.length },
  ...VERSIONS.map((v) => ({
    key: String(v),
    label: `Odoo ${v}`,
    count: RECORDS.filter((r) => r.odooVersion === v).length,
  })),
]

export const SEARCH = {
  label: 'Search the library',
  placeholder: 'Describe the requirement in the client’s own words…',
  hint: 'Plain language — “price per store”, “promotions clash”, “e-invoicing France”.',
  clearLabel: 'Clear search and filters',
  verticalLabel: 'Vertical',
  verticalLegend: 'Filter patterns by vertical',
  versionLabel: 'Odoo version',
  versionLegend: 'Filter patterns by Odoo version',
} as const

const searchText = (r: LibraryRecord): string =>
  [r.pattern, r.detail, r.vertical, `odoo ${r.odooVersion}`, r.solvedBy, r.solution, ...r.keywords]
    .join(' ')
    .toLowerCase()

/** Every term the consultant typed has to appear somewhere in the record. */
export const filterRecords = (
  records: readonly LibraryRecord[],
  query: string,
  vertical: string,
  version: string
): readonly LibraryRecord[] => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
  return records.filter((r) => {
    if (vertical !== ALL && r.vertical !== vertical) return false
    if (version !== ALL && String(r.odooVersion) !== version) return false
    if (terms.length === 0) return true
    const haystack = searchText(r)
    return terms.every((t) => haystack.includes(t))
  })
}

export const totalEffort = (records: readonly LibraryRecord[]): number =>
  records.reduce((sum, r) => sum + r.effortH, 0)

/* ── The table ──────────────────────────────────────────────────────────── */

export const TABLE = {
  label: 'Anonymised records',
  heading: 'How the group has answered this before',
  caption:
    'No client, no project, no place — the pattern, the version it was answered on, and what the answer cost.',
  columns: {
    pattern: 'Requirement pattern',
    vertical: 'Vertical',
    odoo: 'Odoo',
    solvedBy: 'Solved by',
    effort: 'Effort',
    reusable: 'Reusable',
  },
  effortUnit: 'h',
  effortHeaderNote: '(h)',
  odooLabel: (v: OdooVersion): string => `Odoo ${v}`,
  solutionLead: 'via',
  reuseLine: (n: number): string => `Reused ${thinSpace(n)}×`,
  reuseNone: 'Candidate — first build',
  linkHint: 'open the answer in the template & module library',
  emptyLine: (query: string): string =>
    `No pattern in the library matches “${query}”. Widen the search, or clear it to see all patterns.`,
  emptyFiltersLine: 'No pattern matches the filters you have set. Clear them to see all patterns.',
  shownLine: (shown: number, total: number): string =>
    `Showing ${thinSpace(shown)} of ${thinSpace(total)} exemplar patterns`,
  effortLine: (hours: number): string => `${thinSpace(hours)} h of recorded effort on the rows shown`,
} as const

/* ── KPI strip ──────────────────────────────────────────────────────────── */

export interface Kpi {
  key: string
  label: string
  figure: string
  qualifier: string
}

export const KPIS: readonly Kpi[] = [
  {
    key: 'reuse',
    label: 'Portfolio reuse rate',
    figure: `${PORTFOLIO.reuseRatePct}%`,
    qualifier: 'of what the group delivers is answered from the library',
  },
  {
    key: 'no-new-code',
    label: 'Answered without new code',
    figure: `${FIT_GAP.standardPct + FIT_GAP.libraryPct}%`,
    qualifier: `${FIT_GAP.standardPct}% standard Odoo · ${FIT_GAP.libraryPct}% a library module`,
  },
  {
    key: 'patterns',
    label: 'Patterns shown',
    figure: thinSpace(RECORDS.length),
    qualifier: 'exemplars of the group’s full register',
  },
]

/* ── Panel: what this changes in scoping (mint — what PX proposes) ──────── */

export const SCOPING = {
  label: 'What this changes in scoping',
  heading: '4 of 5 requirements are already solved',
  lead:
    '4 of 5 requirements in a new retail scope are already solved somewhere in the group. The consultant is ' +
    'choosing between known answers with known costs, not estimating from a blank page.',
  reuseLine: `The portfolio already reuses ${PORTFOLIO.reuseRatePct}% of what it delivers`,
  reuseLinkLabel: 'see it across all projects',
  reuseHref: HREFS.projects,
  closing: LIBRARY_PAGE.claim,
} as const

/* ── Panel: standard vs custom ──────────────────────────────────────────── */

export type SplitKey = 'standard' | 'library' | 'new-build'

export interface SplitSegment {
  key: SplitKey
  label: string
  pct: number
}

export const SPLIT_SEGMENTS: readonly SplitSegment[] = [
  { key: 'standard', label: 'Standard Odoo', pct: FIT_GAP.standardPct },
  { key: 'library', label: 'Library module', pct: FIT_GAP.libraryPct },
  { key: 'new-build', label: 'New build', pct: FIT_GAP.newBuildPct },
] as const

export const SPLIT = {
  label: 'Standard vs custom',
  heading: 'Where the group’s requirements land',
  barLabel: 'Standard, library and new-build split across the library',
  segmentLine: (s: SplitSegment): string => `${s.pct}%`,
  note:
    `Across the library, ${FIT_GAP.standardPct}% of requirements need no code at all and ${FIT_GAP.libraryPct}% ` +
    `are answered by a module PX has already built; only ${FIT_GAP.newBuildPct}% is genuinely new.`,
  exemplarNote:
    'The group split and the exemplar retail register happen to carry the same three figures — the register is ' +
    'a representative slice, not an exception.',
} as const

/* ── Panel: privacy ─────────────────────────────────────────────────────── */

export const PRIVACY = {
  label: 'Privacy',
  heading: 'What is stripped, and what travels',
  strippings: [
    'Client names are stripped when a requirement enters the library.',
    'Client data is stripped with them — no records, no exports, no attachments.',
    'Only the pattern and the effort travel.',
  ],
  consentLine: 'Consent is recorded per contract',
  consentLinkLabel: 'Clients & AMS',
  consentHref: HREFS.clients,
} as const

/* ── Density ────────────────────────────────────────────────────────────── */

export const DENSITY_NOTE =
  `${thinSpace(RECORDS.length)} exemplar patterns of the group’s full register are shown; searching is the way ` +
  'in, because the library answers a question rather than asking to be read. Source projects are deliberately ' +
  'unnamed — the privacy is the design, not a gap in the data.'
